import { useState } from "owa-react";
import React from "react";
import loc from "owa-localize";
import { observer } from "owa-mobx-react";
import { Button, Popover, PopoverSurface, PopoverTrigger, Tooltip, } from "@fluentui/react-components";
import type { OpenPopoverEvents, OnOpenChangeData } from "@fluentui/react-components";
import { ArrowCurveDownLeftFilled, ArrowCurveDownLeftRegular, bundleIcon, } from "@fluentui/react-icons";
import getMasterCategoryList from "owa-categories/lib/utils/getMasterCategoryList";
import { getDensityModeCssClass } from "owa-fabric-theme";
import { getStore as getListViewStore, type SortColumn, getListViewState, onAutoOpenJumpToMutator, setIsFocusWithinJumpToMutator } from "owa-mail-list-store";
import { resetFocus } from "owa-mail-focus-manager";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { shouldShowListView, useIconFillOnHover } from "owa-mail-layout";
import { jumpToButtonLabel } from "./MailListJumpToPopover.locstring.json";
import { jumpToButton, jumpToButtonWithLabel, jumpToButtonOpened, jumpToIcon, popoverSurface, full, medium, compact, bleedThroughBackground, denseless, jumpToButtonWithLabelDenseless, } from "./MailListJumpToPopover.scss";
import JumpToCalloutContent from "./JumpToCalloutContent";
import getJumpToSortInfo from "../utils/getJumpToSortInfo";

import classnames from "owa-classnames";
import { useIsTypeRampUpdatesEnabled } from "owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled";

const ArrowCurveDownLeftIcon = bundleIcon(ArrowCurveDownLeftFilled, ArrowCurveDownLeftRegular);

export interface MailListJumpToPopoverProps {
    tableViewId: string;
    showButtonLabel: boolean;
    isBleedThrough?: boolean;
}

const defaultExport: React.FunctionComponent<MailListJumpToPopoverProps> = observer(function MailListJumpToPopover(props: MailListJumpToPopoverProps) {
    const { showButtonLabel, tableViewId, isBleedThrough } = props;
    const tableView = getListViewStore().tableViews.get(tableViewId);
    const mailboxInfo = getMailboxInfo(tableView);
    const sortColumn = getJumpToSortInfo(tableView)?.sortColumn;
    const categoryList = getMasterCategoryList(mailboxInfo);
    const densityMode = getDensityModeCssClass(full, medium, compact);
    const { filled, iconColorClassName, onMouseEnter, onMouseLeave } = useIconFillOnHover();
    const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();
    const listViewState = getListViewState();
    const [open, setOpen] = useState(false);
    
    React.useEffect(() => {
        // When the user switches to sort type From or Subject, we want to auto-open the jump to popover and place focus in it.
        // We only want to do this once the table has loaded.
        if (tableView?.isInitialLoadComplete) {
            // This setTimeout is needed because the ML calls resetFocus based on the same tableView prop so we need to
            // time this to be after so this gets focus.
            setTimeout(() => {
                if (listViewState.shouldAutoOpenJumpTo) {
                    setOpen(true);
                    onAutoOpenJumpToMutator();
                }
            }, 10);
        }
    }, [listViewState.shouldAutoOpenJumpTo, tableView?.isInitialLoadComplete]);
    
    const handleOpenChange = React.useCallback((_e: OpenPopoverEvents, data: OnOpenChangeData) => {
        setOpen(data.open || false);
        setIsFocusWithinJumpToMutator(data.open);
    }, []);
    
    const onKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            setOpen(false);
            resetFocus("JumpToCalloutDismissed");
        }
    }, []);
    
    const onClose = React.useCallback(() => {
        setOpen(false);
        setIsFocusWithinJumpToMutator(false);
        resetFocus("JumpToCalloutDismissed");
    }, []);
    
    // Close the jump-to popover when the list view is hidden by responsive layout
    // (e.g. window resized small while reading pane is open). The PopoverSurface
    // renders via a Fluent UI portal, so it stays visible even though the
    // ListPane container is hidden with visibility:hidden.
    const isListViewShown = shouldShowListView();
    
    // If the jump to button is being used to display the label, then we don't want
    // to wrap it in a Tooltip. Otherwise, we do so that we can display the label
    // when the user hovers over the button.
    const menuButton = React.useMemo(() => {
        /* oxlint-disable-next-line react-perf/jsx-no-jsx-as-prop -- (https://aka.ms/OWALintWiki)
         * Fix this by hoisting or memoizing the JSX element so its identity remains stable across renders.
         * > JSX attribute values should not contain other JSX. */
        const jumpToButtonIcon = (<ArrowCurveDownLeftIcon filled={filled} className={classnames(jumpToIcon, isRPDenselessEnabled ? denseless : densityMode, iconColorClassName)}/>);
        
        if (showButtonLabel) {
            return (<Button icon={jumpToButtonIcon} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={classnames(jumpToButtonWithLabel, open && jumpToButtonOpened, isRPDenselessEnabled ? jumpToButtonWithLabelDenseless : densityMode, isBleedThrough && bleedThroughBackground)}>
                        {loc(jumpToButtonLabel)}
                    </Button>);
        }
        else {
            return (<Tooltip content={loc(jumpToButtonLabel)} relationship="label">
                        <Button icon={jumpToButtonIcon} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={classnames(jumpToButton, open && jumpToButtonOpened, isRPDenselessEnabled ? denseless : densityMode, isBleedThrough && bleedThroughBackground)}/>
                    </Tooltip>);
        }
    }, [
        showButtonLabel,
        open,
        densityMode,
        filled,
        iconColorClassName,
        onMouseEnter,
        onMouseLeave,
        isRPDenselessEnabled,
        isBleedThrough,
    ]);
    
    if (!tableView || (sortColumn === 14 && categoryList.length === 0)) {
        return null;
    }
    
    return (<Popover trapFocus={true} positioning={"below-start"} open={open && isListViewShown} onOpenChange={handleOpenChange} closeOnScroll={true}>
                <PopoverTrigger disableButtonEnhancement={true}>{menuButton}</PopoverTrigger>
                <PopoverSurface className={popoverSurface} role={"group"} aria-modal={undefined}>
                    <JumpToCalloutContent mailboxInfo={mailboxInfo} tableView={tableView} onClose={onClose} onKeyDownCallback={onKeyDown}/>
                </PopoverSurface>
            </Popover>);
}, "MailListJumpToPopover");
export default defaultExport;
